// WB-M2A S1 (doc 31 §12.7, §9) -- Board → AI Tutor برنا: the snapshot core.
//
// A snapshot is a PICTURE of part of the board, rendered in this browser
// from the board's own elements by the export the drawing library ships
// (`exportToBlob`), and nothing else: no scene JSON, no element id, no
// stroke point and no equation source ever leaves the board through it.
// What the picture shows is decided HERE, by two rules the S0 spike proved
// (doc 31 §12.5):
//
//   SELECTION  one or more elements are selected: those elements, the text
//              bound inside a selected container (a label), and an arrow
//              bound at BOTH ends to selected elements. Nothing else of the
//              board, selected or not, visible or not.
//   VIEWPORT   nothing is selected: every element that reaches into the
//              visible board. A partially visible element is drawn WHOLE
//              (its offscreen part too) -- the preview shows exactly that.
//              An element wholly outside the visible board is never drawn.
//
// The export profile is the one the spike measured on the real transcriber:
// PNG, white background, 24 px padding, the longest side at most 1568 px,
// scaled up (at most 2x) for a small scope so thin ink survives. The scope,
// the bounds and the picture are frozen together into one share record
// (`freezeBoardShare`) that a retry sends again as it is: a snapshot is
// never taken a second time behind the Student's back.
//
// Pure: nothing here touches the editor, the store or the network. The
// editor passes its elements, app state, files and the export function in.

import { mintRequestId } from './requestId'

export const BOARD_SNAPSHOT_SCOPES = Object.freeze(['selection', 'viewport'])

export const BOARD_SNAPSHOT_PROFILE = Object.freeze({
  mimeType: 'image/png',
  background: '#ffffff',
  exportPadding: 24,
  maxWidthOrHeight: 1568,
  maxScale: 2,
  minScale: 0.25,
})

// Above this the picture is still sent (the upstream accepts 10 MB), but the
// share reports it so the Student can be told the board part is large.
export const BOARD_SNAPSHOT_SOFT_MAX_BYTES = 2 * 1024 * 1024

// The upright box an element surely lies in: its own box, or -- turned --
// the circle around its centre that the turned box cannot leave (the same
// rule `boardView.js` uses to decide what is in view).
export function elementReach(element) {
  const width = Math.abs(element.width) || 0
  const height = Math.abs(element.height) || 0
  const x = Math.min(element.x, element.x + (element.width || 0))
  const y = Math.min(element.y, element.y + (element.height || 0))
  if (!element.angle) return { x, y, width, height }
  const radius = Math.hypot(width, height) / 2
  const centre = { x: x + width / 2, y: y + height / 2 }
  return {
    x: centre.x - radius,
    y: centre.y - radius,
    width: radius * 2,
    height: radius * 2,
  }
}

function isBox(box) {
  return (
    !!box &&
    [box.x, box.y, box.width, box.height].every(Number.isFinite) &&
    box.width > 0 &&
    box.height > 0
  )
}

function touches(box, visible) {
  return (
    box.x <= visible.x + visible.width &&
    box.x + box.width >= visible.x &&
    box.y <= visible.y + visible.height &&
    box.y + box.height >= visible.y
  )
}

function liveElements(elements) {
  return (elements || []).filter(
    element =>
      !!element &&
      !element.isDeleted &&
      typeof element.id === 'string' &&
      Number.isFinite(element.x) &&
      Number.isFinite(element.y)
  )
}

/**
 * The SELECTION rule. Returns the ids of the elements in the picture:
 * the selected ones, the text bound inside a selected container, and every
 * arrow bound at both ends to elements already in the picture (with the
 * text bound to such an arrow). An arrow bound at one end only, or to an
 * unselected element, is not part of the selection's meaning and stays out.
 * @param {object[]} elements -- the live elements of the scene
 * @param {Object.<string, boolean>} selectedElementIds -- the editor's
 * @returns {string[]} in scene order; empty when nothing is selected
 */
export function selectionScopeIds(elements, selectedElementIds) {
  const live = liveElements(elements)
  const selected = selectedElementIds || {}
  const ids = new Set(
    live.filter(element => selected[element.id] === true).map(e => e.id)
  )
  if (ids.size === 0) return []
  // A label inside a selected container.
  live.forEach(element => {
    if (
      element.type === 'text' &&
      typeof element.containerId === 'string' &&
      ids.has(element.containerId)
    ) {
      ids.add(element.id)
    }
  })
  // An arrow between two things in the picture, and its own label.
  live.forEach(element => {
    if (
      element.type === 'arrow' &&
      element.startBinding &&
      element.endBinding &&
      ids.has(element.startBinding.elementId) &&
      ids.has(element.endBinding.elementId)
    ) {
      ids.add(element.id)
    }
  })
  live.forEach(element => {
    if (
      element.type === 'text' &&
      typeof element.containerId === 'string' &&
      ids.has(element.containerId)
    ) {
      ids.add(element.id)
    }
  })
  return live.filter(element => ids.has(element.id)).map(e => e.id)
}

/**
 * The VIEWPORT rule: every element that reaches into the visible board,
 * whole. Nothing when the view cannot be measured.
 * @param {object[]} elements
 * @param {{x: number, y: number, width: number, height: number}} visible --
 *   the visible board in scene coordinates (`BoardEditor.visibleBoard`)
 * @returns {string[]}
 */
export function viewportScopeIds(elements, visible) {
  if (!isBox(visible)) return []
  return liveElements(elements)
    .filter(element => touches(elementReach(element), visible))
    .map(element => element.id)
}

/**
 * The upright box around a set of elements, in scene coordinates (what the
 * provenance records as the snapshot's bounds).
 * @param {object[]} elements
 * @returns {{x: number, y: number, width: number, height: number}|null}
 */
export function snapshotBounds(elements) {
  const live = liveElements(elements)
  if (live.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  live.forEach(element => {
    const box = elementReach(element)
    minX = Math.min(minX, box.x)
    minY = Math.min(minY, box.y)
    maxX = Math.max(maxX, box.x + box.width)
    maxY = Math.max(maxY, box.y + box.height)
  })
  const round = value => Math.round(value * 100) / 100
  return {
    x: round(minX),
    y: round(minY),
    width: round(maxX - minX),
    height: round(maxY - minY),
  }
}

/**
 * Decides what the picture shows: the selection when there is one,
 * otherwise the visible board. `null` when nothing would be in it.
 * @param {{elements: object[], selectedElementIds?: object,
 *   visible: {x: number, y: number, width: number, height: number}}} input
 * @returns {{scope: 'selection'|'viewport', elements: object[],
 *   elementIds: string[], bounds: object, selectedCount: number}|null}
 */
export function resolveSnapshotScope({
  elements,
  selectedElementIds,
  visible,
}) {
  const live = liveElements(elements)
  const selectedIds = selectionScopeIds(live, selectedElementIds)
  const selectedCount = live.filter(
    element => (selectedElementIds || {})[element.id] === true
  ).length
  let scope = 'selection'
  let ids = selectedIds
  if (ids.length === 0) {
    scope = 'viewport'
    ids = viewportScopeIds(live, visible)
  }
  if (ids.length === 0) return null
  const chosen = new Set(ids)
  const inScope = live.filter(element => chosen.has(element.id))
  return {
    scope,
    elements: inScope,
    elementIds: inScope.map(element => element.id),
    bounds: snapshotBounds(inScope),
    selectedCount: scope === 'selection' ? selectedCount : 0,
  }
}

/**
 * The export's size policy: the longest side at most `maxWidthOrHeight`,
 * at most `maxScale` times the scene size (so a small scope is drawn
 * larger, never blurred), never below `minScale`.
 * @param {number} width -- the scene width of the picture (padding included)
 * @param {number} height
 * @param {typeof BOARD_SNAPSHOT_PROFILE} [profile]
 * @returns {{width: number, height: number, scale: number}}
 */
export function snapshotDimensions(
  width,
  height,
  profile = BOARD_SNAPSHOT_PROFILE
) {
  const longest = Math.max(width, height, 1)
  const scale = Math.max(
    profile.minScale,
    Math.min(profile.maxScale, profile.maxWidthOrHeight / longest)
  )
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
    scale,
  }
}

/**
 * Only the files the picture's own image elements show (an equation's
 * picture); nothing of any other element's.
 * @param {object[]} elements -- the elements in the picture
 * @param {Object.<string, object>} files -- the editor's files
 * @returns {Object.<string, object>}
 */
export function snapshotFiles(elements, files) {
  const out = {}
  const all = files || {}
  ;(elements || []).forEach(element => {
    if (
      element &&
      element.type === 'image' &&
      typeof element.fileId === 'string' &&
      all[element.fileId]
    ) {
      out[element.fileId] = all[element.fileId]
    }
  })
  return out
}

/**
 * Renders the picture through the drawing library's own export. The
 * elements are the ones `resolveSnapshotScope` chose; the app state is the
 * editor's (the export reads the theme and the zoom from it, never the
 * selection), the files only those the picture shows.
 * @param {{elements: object[], appState: object, files: object,
 *   exportToBlob: function(object): Promise<Blob>,
 *   profile?: typeof BOARD_SNAPSHOT_PROFILE}} input
 * @returns {Promise<{blob: Blob, width: number, height: number, bytes: number,
 *   scale: number, exportMs: number}>}
 */
export async function renderBoardSnapshot({
  elements,
  appState,
  files,
  exportToBlob,
  profile = BOARD_SNAPSHOT_PROFILE,
}) {
  if (typeof exportToBlob !== 'function') {
    throw new Error('renderBoardSnapshot: exportToBlob is required')
  }
  let dimensions = null
  const started =
    typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now()
  const blob = await exportToBlob({
    elements,
    appState: {
      ...(appState || {}),
      exportBackground: true,
      viewBackgroundColor: profile.background,
      exportWithDarkMode: false,
      exportEmbedScene: false,
      exportScale: 1,
      selectedElementIds: {},
    },
    files: snapshotFiles(elements, files),
    exportPadding: profile.exportPadding,
    getDimensions: (width, height) => {
      dimensions = snapshotDimensions(width, height, profile)
      return dimensions
    },
    mimeType: profile.mimeType,
    quality: 1,
  })
  const finished =
    typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now()
  return {
    blob,
    width: dimensions ? dimensions.width : 0,
    height: dimensions ? dimensions.height : 0,
    scale: dimensions ? dimensions.scale : 1,
    bytes: blob && typeof blob.size === 'number' ? blob.size : 0,
    exportMs: Math.round(finished - started),
  }
}

/** A stable client share id: a UUID v4, minted once per snapshot. */
export function createShareId() {
  return mintRequestId()
}

/**
 * Freezes one share: the picture and everything that describes it, as it
 * was at this moment. A retry sends THIS object; nothing of it can change.
 * @param {{shareId?: string, scope: string, elementIds: string[], bounds: object,
 *   selectedCount?: number, blob: Blob, width: number, height: number,
 *   bytes: number, boardId: string|null, boardVersion: number|null,
 *   sceneVersion: number, previewUrl?: string}} input
 * @returns {Readonly<object>}
 */
export function freezeBoardShare(input) {
  const share = {
    shareId: input.shareId || createShareId(),
    scope: input.scope,
    elementIds: Object.freeze((input.elementIds || []).slice()),
    bounds: Object.freeze({ ...(input.bounds || {}) }),
    selectedCount: input.selectedCount || 0,
    blob: input.blob,
    width: input.width,
    height: input.height,
    bytes: input.bytes,
    overSoftLimit: input.bytes > BOARD_SNAPSHOT_SOFT_MAX_BYTES,
    boardId: input.boardId || null,
    boardVersion: Number.isInteger(input.boardVersion)
      ? input.boardVersion
      : null,
    sceneVersion: input.sceneVersion,
    previewUrl: input.previewUrl || '',
    createdAt: new Date().toISOString(),
  }
  return Object.freeze(share)
}
